import React from "react";
import PropTypes from "prop-types";

import _ from 'lodash';

import ProductImageCarouselWrapper from '../../shared/ProductImageCarouselWrapper';
import TraitColorPicker from './TraitColorPicker';
import TraitColorDropdown from './TraitColorDropdown';
import TraitSelector from '../../shared/TraitSelector';
import { MessagesList } from '../../../SharedComponents/UserMessaging';
import QuantityInput from '../../shared/QuantityInput';
import ProductDetailTabs from '../../shared/ProductDetailTabs';
import ProductPriceAndInventory from '../../shared/ProductPriceAndInventory';
import AddToWishlistApp from '../../../addtowishlist/AddToWishlistApp';
import { addResourceStrings, translateResourceString } from '../../../util/translationUtility'

const variationToImages = (variation) => ({
    large: variation.LargeImages,
    primary: variation.PrimaryImages,
    name: variation.Name,
    code: variation.Code
});

class ProductComponents extends React.Component {
    constructor(props) {
        super(props);

        this.initialTraitSelections = this.props.traits.map(_trait => null);
        this.initialTraitMasks = this.props.traits.map(trait => trait.traitMasks);

        addResourceStrings(this.props.wishlistModel.LocalizationItems);

        this.state = {
            currentVariation: this.props.variations[0],
            filteredVariations: this.props.filteredVariations,
            quantity: 1,
            // clone these arrays for state
            traitSelections: this.initialTraitSelections.slice(),
            traitMasks: this.initialTraitMasks,
            customerType: this.props.customerType,
            wishlistLinkClass: 'fa fa-bookmark',
            wishlistSrText: '',
            wishlistLinkText: translateResourceString("/Product/AddToListButton")
        };

        this.initialState = this.state;

        this.updateSelectedSku = this.updateSelectedSku.bind(this);
        this.updateSelectedColor = this.updateSelectedColor.bind(this);
        this.findTraitValueIndexes = this.findTraitValueIndexes.bind(this);
        this.findTraitValueMasks = this.findTraitValueMasks.bind(this);
        this.computeSelectionStateUpdate = this.computeSelectionStateUpdate.bind(this);
        this.resetInitialState = this.resetInitialState.bind(this);
        this.onQuantityUpdate = this.onQuantityUpdate.bind(this);
        this.updateWishlistLinkClass = this.updateWishlistLinkClass.bind(this);
        this.updateWishlistSrText = this.updateWishlistSrText.bind(this);
        this.updateWishlistLinkText = this.updateWishlistLinkText.bind(this);

        // Don't bind the fn generator. We shouldn't be looking at state when generating
        // functions unless we know for sure when the function will be called?
        this.onTraitSelectedFns = this.props.traits.map((_trait, i) => this.makeTraitSelectionListener({
            dim: i
        }).bind(this));

        for (var i = 0; i < this.state.traitSelections.length; i++) {
            if (this.props[`trait${i + 1}Selected`] !== null) {
                var stateUpdate = this.computeSelectionStateUpdate(i, this.props[`trait${i + 1}Selected`], this.state.traitSelections, this.props.traits, this.props.variations, this.state.traitMasks);
                this.state = { ...this.state, ...stateUpdate };
            }
        }
    }

    makeTraitSelectionListener({ dim }) {
        const self = this;
        return function onTraitIndexSelected(index) {
            self.setState(this.computeSelectionStateUpdate(dim, index, this.state.traitSelections, this.props.traits, this.props.variations, this.state.traitMasks));
        };
    }

    onQuantityUpdate(val) {
        var parsed = Number.parseInt(val, 10);
        if (Number.isNaN(parsed) || parsed < 1) {
            parsed = 0;
        }
        this.setState({ quantity: parsed });
    }

    computeSelectionStateUpdate(dim, index, traitSelections, traits, variations, traitMask) {
        traitSelections = traitSelections.slice();
        let currentVariation = this.state.currentVariation;

        if (Number.isNaN(index)) {   //Clicked on Select         
            traitSelections[dim] = null;
        } else {
            traitSelections[dim] = index;
        }

        const colorTraitValue = currentVariation.ColorTraitValue;
        const selectedValues = traitSelections.map((index, i) => index === null ? null : traits[i].values[index]);

        // Filter for the variations that match all the currently selected trait values
        let filteredVariations = variations
            .filter(variation => variation.ColorTraitValue === colorTraitValue)
            // look at each trait for each variation to see 
            .filter(variation =>
                traits.every((trait, i) =>
                    selectedValues[i] === null ||
                    variation[trait.valueKey] === selectedValues[i]));

        // When there are 2 traits, if the new trait selection invalidates the other trait selection, remove the other trait selection
        if (traitSelections.length === 2 && filteredVariations.length === 0) {
            const i = dim === 0 ? 1 : 0;
            traitSelections[i] = null;
            selectedValues[i] = null;

            filteredVariations = variations
                .filter(variation => variation.ColorTraitValue === colorTraitValue)
                // look at each trait for each variation to see 
                .filter(variation =>
                    traits.every((trait, i) =>
                        selectedValues[i] === null ||
                        variation[trait.valueKey] === selectedValues[i]));
        }

        // Figure out what trait values to show based on what the user has already chosen
        const traitMasks = traitSelections.includes(null)
            ? traits.map((trait, i) => {
                if (traitSelections[i] === null) {
                    // No selection, so get the values for this trait from the remaining variations 
                    const remainingValues = filteredVariations.map(variation => variation[trait.valueKey]);
                    return trait.values.map(value => remainingValues.includes(value));
                }

                // User already made selection, so show all in color group in case they want to change
                const colorVariations = variations.filter(val => val.ColorTraitValue === colorTraitValue);
                const remainingValues = colorVariations.map(variation => variation[trait.valueKey]);

                return trait.values.map(value => remainingValues.includes(value));
            })
            : traitMask;

        if (filteredVariations.length === 0) {
            // No matches. Clear selections and filters.
            return this.initialState;
        }

        if (filteredVariations.length === 1) {
            // One match. Select all the traits for this variation
            currentVariation = filteredVariations[0];
            traitSelections = this.findTraitValueIndexes(currentVariation);
        }

        const filteredSkus = filteredVariations.map(variation => variation.Code);
        if (!filteredSkus.includes(currentVariation.Code)) {
            // Currently selected SKU has been filtered out, select the first available SKU
            currentVariation = filteredVariations[0];
        }

        // reset wishlist button text to initial
        const {wishlistLinkClass, wishlistSrText, wishlistLinkText} = this.initialState;

        return { traitMasks, filteredVariations, traitSelections, currentVariation, wishlistLinkClass, wishlistSrText, wishlistLinkText };
    }

    findTraitValueMasks(variation) {
        let ret = [];
        let colorvariations = this.props.variations.filter(val => val.ColorTraitValue === variation.ColorTraitValue);

        if (typeof variation !== 'undefined') {
            for (let trait of this.props.traits) {
                //Get valid trait values in color selection
                ret.push(trait.values.map(val => colorvariations.map(variation =>
                    variation[trait.valueKey]).filter(value =>
                        value !== null).includes(val) ? true : false));
            }
        }

        return ret;
    }

    findTraitValueIndexes(variation) {
        let ret = [];

        if (typeof variation !== 'undefined') {
            for (let trait of this.props.traits) {
                ret.push(trait.values.indexOf(variation[trait.valueKey]));
            }
        }

        return ret;
    }

    updateSelectedSku(sku) {
        if (sku === null) {
            return;
        }

        const selectedVariation = this.props.variations.find(variation => variation.Code === sku);

        if (typeof selectedVariation === 'undefined' || selectedVariation === null) {
            return this.initialState;
        }

        const selections = this.findTraitValueIndexes(selectedVariation);

        // reset wishlist button text to initial
        const {wishlistLinkClass, wishlistSrText, wishlistLinkText} = this.initialState;

        return {
            traitSelections: selections,
            currentVariation: selectedVariation,
            wishlistLinkClass,
            wishlistSrText,
            wishlistLinkText
        };
    }

    updateSelectedColor(color) {
        if (color === null) {
            return;
        }

        const filteredVariations = this.props.variations.filter(v => v.ColorTraitValue === color);
        const { traitSelections, currentVariation, wishlistLinkClass, 
            wishlistSrText, wishlistLinkText } = this.updateSelectedSku(filteredVariations[0].Code);

        return {
            traitMasks: this.findTraitValueMasks(currentVariation),
            // Color selection changed, clear selected traits if more than 1 variation
            traitSelections: filteredVariations.length > 1 ? this.initialTraitSelections : traitSelections,
            filteredVariations,
            currentVariation,
            wishlistLinkClass,
            wishlistSrText,
            wishlistLinkText
        };
    }

    updateWishlistLinkClass(linkClass){
        this.setState({wishlistLinkClass: linkClass});
    }

    updateWishlistSrText(srText){
        this.setState({wishlistSrText: srText});
    }

    updateWishlistLinkText(linkText){
        this.setState({wishlistLinkText: linkText});
    }

    resetInitialState() {
        this.setState(this.initialState);
    }

    render() {
        const ReactTooltip =  typeof(window) !== 'undefined' && !window.isServerSide && require('react-tooltip').default;
        const { currentVariation, filteredVariations, traitSelections, traitMasks, quantity, 
            customerType, wishlistLinkClass, wishlistSrText, wishlistLinkText  } = this.state;
        const { assets, blogs, dealerDocuments, documentWarranties, gallery, videos, traits, isColorPicker, siteName, wishlistModel } = this.props;
        const variations = this.props.colorVariations;
        const hasTrait0Values = typeof traits[0] !== 'undefined',
            hasTrait1Values = typeof traits[1] !== 'undefined';

        var hasInventoryDetails = !!currentVariation.InventoryDetails;
        let maxQuantity = hasInventoryDetails ? currentVariation.InventoryDetails.TotalInStock : 0;

        const layout = {
            showPriceAndInventory: !traitSelections.includes(null),
            showTrait0Dropdown: hasTrait0Values && (hasTrait1Values || !isColorPicker),
            showTrait1Dropdown: hasTrait1Values && !isColorPicker,
            showQuoteButton: siteName === 'Dealer'
                ? currentVariation.ShowQuoteButton
                : currentVariation.ShowQuoteButton && customerType === 'R',
            showBuyButton: currentVariation.ShowBuyButton && hasInventoryDetails,
            disableCartButton: traitSelections.includes(null) || quantity < 1 || maxQuantity <= 0,
            disableAddToList: traitSelections.includes(null)
        };

        if (!layout.ShowBuyButton && layout.showQuoteButton) {
            maxQuantity = 99999;
        }

        return (
            <div className='product-wrapper cabinet-product-wrapper'>
                <div className="container">
                    <div className='product-details-wrapper'>
                        <div className='row'>

                            <div className='col-xs-12 js-variation'>
                                <div className='variation-header'>
                                    <h1>{currentVariation.Name}</h1>
                                    <p><b>Item Number:</b> {currentVariation.BRPART}</p>
                                </div>
                            </div>

                            <div className='product-details-left col-xs-12 col-sm-6 col-lg-6'>
                                <div className='row'>
                                    <div className='col-xs-12'>
                                        <ProductImageCarouselWrapper images={variationToImages(currentVariation)} fallbackTitle={currentVariation.Name} />
                                    </div>
                                    <div className='col-xs-12'>
                                        <TraitColorPicker onSelect={(color) => this.setState(this.updateSelectedColor(color))} variations={variations} currentVariation={currentVariation} />
                                    </div>
                                </div>
                            </div>

                            <div className='product-details-right col-xs-12 col-sm-6 col-lg-6'>
                                <div className='row'>

                                    {layout.showPriceAndInventory && <ProductPriceAndInventory currentVariation={currentVariation} />}

                                    <div className='col-xs-12 js-variation'>
                                        <div className='variation-details' id="variation-details">
                                            <div dangerouslySetInnerHTML={{ __html: currentVariation.Description }} />
                                        </div>
                                    </div>

                                    <div className='col-xs-12 js-add-to-cart'>
                                        <div className='addtocart-wrapper text-left'>
                                            <form role="form" className='form-horizontal'>
                                                <TraitColorDropdown
                                                    onSelect={(color) => this.setState(this.updateSelectedColor(color))}
                                                    variations={variations}
                                                    currentVariation={currentVariation}
                                                    colorLabel={this.props.colorDropdownLabel}
                                                />

                                                {traits.filter(trait => trait.values.length > 0).map((trait, i) => (
                                                    layout[`showTrait${i}Dropdown`] &&
                                                    <TraitSelector
                                                        onSelect={this.onTraitSelectedFns[i]}
                                                        label={trait.label}
                                                        values={trait.values}
                                                        currentValue={traitSelections[i]}
                                                        n={i}
                                                        valueMask={traitMasks[i]}
                                                        key={`trait${i}`}
                                                    />
                                                ))}

                                                <div className='form-group'>
                                                    <div className='col-xs-12'>
                                                        <a href="" onClick={(e) => { e.preventDefault(); this.resetInitialState(); }}>Clear Selections</a>
                                                    </div>
                                                </div>

                                                {filteredVariations.length > 1 && !traitSelections.includes(null) &&
                                                    <div className='form-group'>
                                                        <div className='col-xs-12'>
                                                            <label className='' htmlFor='skuvalue'>Item</label>
                                                            <select
                                                                id="skuvalue"
                                                                className='form-control col-xs-12'
                                                                value={currentVariation.Code}
                                                                onChange={(e) => this.setState(this.updateSelectedSku(e.target.value))}
                                                            >
                                                                {filteredVariations.map(v => <option key={v.Code} value={v.Code}>{v.Code}</option>)}
                                                            </select>
                                                        </div>
                                                    </div>
                                                }

                                                <div className='clearfix' />
                                                {(layout.showQuoteButton || layout.showBuyButton) && (
                                                    <div className='form-group'>
                                                        <div className='col-xs-4 col-md-3'>
                                                            <QuantityInput onQuantityUpdate={this.onQuantityUpdate} currentQuantity={quantity} maxQuantity={maxQuantity} />
                                                        </div>
                                                        <div className='col-xs-8 col-md-9'>
                                                            <span data-tip="Select required fields(*) above to enable" data-tip-disable={!layout.disableCartButton}>
                                                                <button
                                                                    type='button'
                                                                    className='add-to-cart-button btn btn-primary mb-2'
                                                                    disabled={layout.disableCartButton}
                                                                    onClick={() => this.props.onAddToCart({
                                                                        variation: currentVariation,
                                                                        quantity: quantity,
                                                                        cartName: layout.showBuyButton ? 'Default' : 'Quote Request'
                                                                    })}
                                                                >
                                                                    {`Add to ${layout.showBuyButton ? 'Cart' : 'Quote'}`}
                                                                </button>
                                                            </span>
                                                            {wishlistModel.ShowWishlist && (
                                                            <span data-tip="Select required fields(*) above to enable" data-tip-disable={!layout.disableCartButton}>
                                                                <AddToWishlistApp
                                                                    Name={currentVariation.Name}
                                                                    SKU={currentVariation.Code}
                                                                    ImageUrl={wishlistModel.ImageUrl}
                                                                    Wishlists={wishlistModel.Wishlists}
                                                                    AddItemEndpoint={wishlistModel.AddItemEndpoint}
                                                                    AddListEndpoint={wishlistModel.AddListEndpoint}
                                                                    UserIsAuthorized={wishlistModel.UserIsAuthorized}
                                                                    LoginLink={wishlistModel.LoginLink}
                                                                    LocalizationItems={wishlistModel.LocalizationItems}
                                                                    Disabled={layout.disableAddToList}
                                                                    linkClass={wishlistLinkClass}
                                                                    setLinkClass={this.updateWishlistLinkClass}
                                                                    srText={wishlistSrText}
                                                                    setSrText={this.updateWishlistSrText}
                                                                    linkText={wishlistLinkText}
                                                                    setLinkText={this.updateWishlistLinkText}>
                                                                </AddToWishlistApp>
                                                            </span>)}
                                                        </div>
                                                        {ReactTooltip && <ReactTooltip />}
                                                    </div>
                                                )}
                                            </form>
                                        </div>
                                        <MessagesList messages={this.props.messages} onDismiss={this.props.onDismissMessage} />
                                    </div>

                                </div>
                            </div>

                        </div>
                    </div>

                    <ProductDetailTabs
                        assets={assets}
                        blogs={blogs}
                        currentVariation={currentVariation}
                        documentWarranties={documentWarranties}
                        gallery={gallery}
                        id="product-detail-tabs"
                        resources={dealerDocuments}
                        videos={videos}
                    />
                </div>
            </div>
        );
    }
}

ProductComponents.propTypes = {
    addToCartEndpoint: PropTypes.string,
    assets: PropTypes.array,
    blogs: PropTypes.object,
    categoryId: PropTypes.number,
    colorVariations: PropTypes.array.isRequired,
    documentWarranties: PropTypes.object,
    gallery: PropTypes.object,
    isColorPicker: PropTypes.bool.isRequired,
    messages: PropTypes.array.isRequired,
    onAddToCart: PropTypes.func.isRequired,
    onDismissMessage: PropTypes.func.isRequired,
    variations: PropTypes.array.isRequired,
    videos: PropTypes.object,
    trait1Selected: PropTypes.string,
    trait2Selected: PropTypes.string,
    customerType: PropTypes.string,
    siteName: PropTypes.string,
    wishlistModel: PropTypes.object
};

ProductComponents.defaultProps = {
    messages: [],
    assets: []
};

export default ProductComponents;